// frontend/src/pdf/PdfOptionsModal.js
import { useState, useMemo, useCallback, Component } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import { renderReportPdf, emailReportPdf, upsertPdfConfig } from '../services/api';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usePdfConfig } from '../context/PdfConfigContext';
import { PDF_REGISTRY, resolvePdfPreferences } from './lib/registry';
import { PDF_THEMES } from './lib/themes';
import { DOCUMENT_MAP } from './usePdfDownload';
import styles from './PdfOptionsModal.module.css';

class PdfErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
                      flexDirection: 'column', gap: 8, color: 'var(--color-danger)', padding: 24 }}>
          <strong>PDF preview failed</strong>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
            {this.state.error?.message || 'An error occurred while rendering the PDF.'}
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}

const THEME_LABELS = {
  saffron:  'Saffron',
  navy:     'Navy',
  emerald:  'Emerald',
  charcoal: 'Charcoal',
  maroon:   'Maroon',
  plain:    'Plain',
  minimal:  'Minimal',
};

/**
 * PdfOptionsModal — shown when registry[type].preview === true.
 *
 * Props:
 *   type           — registry key e.g. 'balanceSheet'
 *   documentProps  — raw data passed to the Document component
 *   onClose        — () => void
 */
export default function PdfOptionsModal({ type, documentProps, onClose, defaultEmailTo }) {
  const config = PDF_REGISTRY[type];
  const { configs, reload } = usePdfConfig();

  const initial = useMemo(
    () => resolvePdfPreferences(configs[type] || null, type),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [theme, setTheme]             = useState(initial.theme);
  const [orientation, setOrient]      = useState(initial.orientation);
  const [sections, setSections]       = useState(initial.sections);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const [downloading, setDownloading] = useState(false);

  const [emailAddress, setEmailAddress] = useState(defaultEmailTo || '');
  const [emailSending, setEmailSending]  = useState(false);

  const handleSendEmail = useCallback(async () => {
    if (!emailAddress.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setEmailSending(true);
    try {
      await emailReportPdf({
        to:          emailAddress.trim(),
        type,
        data:        documentProps,
        theme,
        orientation,
        sections,
        filters:     documentProps?.filters || '',
        reportTitle: config?.label || type,
      });
      toast.success('Report sent by email');
    } catch (err) {
      console.error('PDF email failed', err);
      toast.error(err?.response?.data?.message || 'Failed to send report email');
    } finally {
      setEmailSending(false);
    }
  }, [emailAddress, type, documentProps, theme, orientation, sections, config]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await renderReportPdf({
        type:        type,
        data:        documentProps,
        theme,
        orientation,
        sections,
        filters:     documentProps?.filters || '',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${type}-${Date.now()}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('PDF download failed', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [type, documentProps, theme, orientation, sections]);

  const patchSection = (id, val) =>
    setSections((prev) => ({ ...prev, [id]: val }));

  const handleSaveDefault = async () => {
    setSavingPrefs(true);
    try {
      await upsertPdfConfig(type, { theme, orientation, sections });
      await reload();
      toast.success('PDF defaults saved');
    } catch {
      toast.error('Failed to save PDF defaults');
    } finally {
      setSavingPrefs(false);
    }
  };

  const DocumentComponent = DOCUMENT_MAP[type];

  const docElement = useMemo(() => {
    if (!DocumentComponent) return null;
    return (
      <DocumentComponent
        data={documentProps}
        theme={theme}
        orientation={orientation}
        sections={sections}
        filters={documentProps?.filters || ''}
      />
    );
  }, [DocumentComponent, documentProps, theme, orientation, sections]);

  if (!DocumentComponent) return null;

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Export as PDF: {config.label}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><FiX /></button>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* Options panel */}
          <div className={styles.optionsPanel}>

            {/* Theme */}
            <div className={styles.optionGroup}>
              <span className={styles.optionLabel}>Theme</span>
              <div className={styles.themeOptions}>
                {Object.entries(THEME_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    className={`${styles.themeOption} ${theme === key ? styles.themeOptionActive : ''}`}
                    onClick={() => setTheme(key)}
                  >
                    <span className={styles.themeSwatch} style={{ backgroundColor: PDF_THEMES[key].primary }} />
                    <span className={styles.themeOptionLabel}>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Orientation */}
            <div className={styles.optionGroup}>
              <span className={styles.optionLabel}>Orientation</span>
              <div className={styles.orientRow}>
                {['portrait', 'landscape'].map((o) => (
                  <button
                    key={o}
                    className={`${styles.orientBtn} ${orientation === o ? styles.orientBtnActive : ''}`}
                    onClick={() => setOrient(o)}
                  >
                    {o.charAt(0).toUpperCase() + o.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Sections */}
            <div className={styles.optionGroup}>
              <span className={styles.optionLabel}>Include</span>
              {config.sections.map(({ id, label }) => (
                <label key={id} className={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={!!sections[id]}
                    onChange={(e) => patchSection(id, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>

            {/* Email */}
            <div className={styles.optionGroup}>
              <span className={styles.optionLabel}>Send by Email</span>
              <input
                type="email"
                className={styles.emailInput}
                placeholder="recipient@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSendEmail}
                disabled={emailSending || !emailAddress.trim()}
              >
                {emailSending ? 'Sending…' : 'Send Email'}
              </button>
            </div>

            <button
              className="btn btn-ghost btn-sm"
              onClick={handleSaveDefault}
              disabled={savingPrefs}
              style={{ marginTop: 'auto' }}
            >
              {savingPrefs ? 'Saving…' : 'Save as default'}
            </button>
          </div>

          {/* Live preview */}
          <div className={styles.previewPanel}>
            <PdfErrorBoundary>
              <PDFViewer width="100%" height="100%" showToolbar={false}>
                {docElement}
              </PDFViewer>
            </PdfErrorBoundary>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <div className={styles.footerRight}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
